
import { ChevronRight, Vote, Palette, Leaf } from 'lucide-react';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import { useEffect, useState } from 'react';
import ProverbWidget from '../components/ProverbWidget';
import NewsPreview from '../components/NewsPreview';

const HomePage = () => {
  const [animateJoinButton, setAnimateJoinButton] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      // Animate join button when scrolled more than 300px
      if (window.scrollY > 300) {
        setAnimateJoinButton(true);
      } else {
        setAnimateJoinButton(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return (
    <div className="min-h-screen w-full bg-gray-950" style={{
      backgroundImage: 'url(/lovable-uploads/c991389f-c2d1-4e8c-aee1-419cb7b0f3f0.png)',
      backgroundSize: '100% auto',
      backgroundPosition: 'top center',
      backgroundRepeat: 'no-repeat',
      backgroundColor: '#000',
      backgroundAttachment: 'scroll'
    }}>
      {/* Hero Section with àdìrẹ pattern overlay */}
      <section className="relative h-[80vh] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40 z-10"></div>
        <div className="absolute inset-0 opacity-20 bg-[url('/lovable-uploads/adire-pattern.png')] z-20"></div>
        
        <div className="container-custom relative z-20 h-full flex flex-col justify-center">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white drop-shadow-lg">
                Ọmọ Odùduwà
              </h1>
            </div>
            <p className="text-xl md:text-2xl text-white mb-8 drop-shadow-lg">
              Join our movement to empower Yoruba communities through civic engagement, 
              cultural preservation, and sustainable development.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/get-involved">
                <Button variant="primary" size="lg" className="shadow-lg">
                  Get Involved Today
                </Button>
              </Link>
              <Link to="/mission">
                <Button variant="outline" size="lg" className="border-white hover:bg-white shadow-lg text-white hover:text-slate-900">
                  Learn About Our Mission
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats Section */}
      <section className="py-16 bg-zinc-950/90">
        <div className="container-custom">
          <h2 className="section-title text-center mb-12 text-white drop-shadow-lg">Our Impact</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-amber-400 mb-2 drop-shadow-lg">25+</div>
              <p className="text-xl text-white drop-shadow-md">Communities Served</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-amber-400 mb-2 drop-shadow-lg">1,200+</div>
              <p className="text-xl text-white drop-shadow-md">Active Members</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-amber-400 mb-2 drop-shadow-lg">5+</div>
              <p className="text-xl text-white drop-shadow-md">Civic Projects</p>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="divider-wave bg-gray-950"></div>

      {/* Focus Areas with Icons */}
      <section className="py-16 bg-gray-950/90">
        <div className="container-custom">
          <h2 className="section-title text-center mb-12 text-white drop-shadow-lg">Our Focus Areas</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="rounded-lg overflow-hidden shadow-md border-t-4 border-amber-500 bg-slate-800">
              <div className="p-6">
                <div className="bg-amber-500/20 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4">
                  <Vote className="w-8 h-8 text-amber-400" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white drop-shadow-md">Civic Engagement</h3>
                <p className="text-slate-200 mb-4 drop-shadow-sm">
                  Empowering Yoruba people to actively participate in democratic processes and governance.
                </p>
              </div>
            </div>

            <div className="rounded-lg overflow-hidden shadow-md border-t-4 border-amber-500 bg-slate-800">
              <div className="p-6">
                <div className="bg-amber-500/20 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4">
                  <Palette className="w-8 h-8 text-amber-400" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white drop-shadow-md">Cultural Preservation</h3>
                <p className="text-slate-200 mb-4 drop-shadow-sm">
                  Protecting and promoting Yoruba language, arts, traditions, and heritage.
                </p>
              </div>
            </div>

            <div className="rounded-lg overflow-hidden shadow-md border-t-4 border-amber-500 bg-slate-800">
              <div className="p-6">
                <div className="bg-amber-500/20 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4">
                  <Leaf className="w-8 h-8 text-amber-400" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white drop-shadow-md">Sustainable Development</h3>
                <p className="text-slate-200 mb-4 drop-shadow-sm">
                  Creating economic opportunities and environmental sustainability in our communities.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="divider-wave"></div>

      {/* Latest News Section - Now using NewsPreview component */}
      <NewsPreview />

      {/* Join CTA Section */}
      <section className="relative bg-slate-900/90 text-white py-20 backdrop-blur-sm">
        <div className="absolute inset-0 opacity-20">
          <img alt="Ọmọ Odùduwà logo" src="/lovable-uploads/d2c810e8-aa81-48b3-9dc3-24ac94fa2fcc.jpg" className="w-full h-full opacity-10 object-none" />
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white drop-shadow-lg">Join the Movement</h2>
            <p className="text-xl mb-8 text-slate-200 drop-shadow-md">
              Be a part of Ọmọ Odùduwà and contribute to building a better future for our communities.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/join">
                <Button variant="primary" size="lg" animate={animateJoinButton} className="bg-amber-500 hover:bg-amber-600">
                  Become a Member
                </Button>
              </Link>
              <Link to="/get-involved">
                <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-slate-900">
                  Support Our Mission
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Proverb Widget */}
      <ProverbWidget />
    </div>
  );
};

export default HomePage;
